<template>
    <PageSidebar :showError="v$.$error">
        <template #sidebarTitle> {{ $t("love.menu.review_form") }} </template>
        <template #sidebarMain>
            <CardToggle :title="$t('love.form.content')">
                <div class="space-y-5">
                    <div id="wallAskImg">
                        <FormFiles
                            v-model="files"
                            :title="$t('love.form.image_logo_picture') + '!'"
                            accept="image/*"
                            :labels="{
                                clickToUpload: $t('shared.uploader.click_to_upload'),
                                dragAndDrop: $t('shared.uploader.drag_and_drop'),
                                fileInfo: $t('shared.uploader.file_details'),
                            }"
                            :maxSize="10000"
                            :errorMessages="{
                                invalidSize: $t('shared.invalid.file_size_over'),
                                invalidType: $t('shared.invalid.file_type'),
                            }"
                            :single-file="true"
                            @fileSelected="uploadImg"
                            @fileRemoved="removeImg"
                        />
                    </div>
                    <FormInput
                        id="wallAskTitle"
                        :title="$t('love.form.request_title')"
                        :placeholder="$t('love.form.request_title')"
                        v-model="wall.askTitle"
                    />
                    <div id="wallAskDesc">
                        <FormRichText
                            :title="$t('love.form.request_description')"
                            :placeholder="$t('love.form.request_description')"
                            v-model="wall.askDesc"
                        />
                    </div>
                    <template v-if="wall.author">
                        <FormAvatar
                            id="wallAuthor"
                            :title="$t('love.form.author_avatar')"
                            :updloadLabel="$t('shared.upload')"
                            :deleteLabel="$t('shared.delete')"
                            :file="wall.author.avatar"
                            @loadFile="changeAuthorAvatar($event)"
                        />

                        <FormInput
                            :title="$t('love.form.author_name')"
                            v-model="wall.author.name"
                        />
                        <FormInput
                            :title="$t('love.form.author_position')"
                            v-model="wall.author.position"
                        />
                    </template>
                </div>
            </CardToggle>
            <CardToggle :title="$t('love.form.general_settings')">
                <div class="space-y-5">
                    <FormToggleSm
                        :title="$t('love.form.ask_for_email')"
                        :modelValue="!!wall.askForEmail"
                        @update:modelValue="handleAskForEmailUpdate"
                    />

                    <FormToggleSm
                        :title="$t('love.form.email_required')"
                        :modelValue="!!wall.emailRequired"
                        @update:modelValue="handleEmailRequiredUpdate"
                    />

                    <FormToggleSm
                        :title="$t('love.form.publish_automatically')"
                        :modelValue="!!wall.publishAutomatically"
                        @update:modelValue="wall.publishAutomatically = $event"
                    />
                </div>
            </CardToggle>
            <CardToggle :title="$t('love.form.after_creating')">
                <div class="space-y-5">
                    <FormToggleSm
                        :title="$t('love.form.send_email')"
                        :modelValue="!!wall.sendEmail"
                        @update:modelValue="wall.sendEmail = $event"
                    />

                    <FormInput
                        :title="$t('love.form.redirect_url')"
                        placeholder="url"
                        v-model="wall.redirectUrl"
                    />

                    <FormInput
                        :title="$t('love.form.redirect_button')"
                        placeholder="url"
                        v-model="wall.redirectButton"
                    />
                </div>
            </CardToggle>
            <CardToggle :title="$t('love.form.data_transfer')">
                <div class="space-y-5">
                    <p class="ct-text">{{ $t("love.form.webhook_info") }}</p>
                    <FormInput
                        :title="$t('love.form.webhook_url')"
                        placeholder="url"
                        v-model="wall.webhookUrl"
                        @blur="v$.wall.webhookUrl.$touch"
                        :error="v$.wall.webhookUrl.$errors?.[0]?.$message"
                    />
                </div>
            </CardToggle>
        </template>
        <template #sidebarFooter>
            <cErrorInfo v-if="v$.$error" />
            <cSaveButton
                @save="handleFormSave"
                :disabled="!hasChanges"
                :ctaDisabled="$t('shared.save')"
            />
        </template>
    </PageSidebar>
</template>

<script>
import { mapState, mapActions } from "vuex";
import { PageSidebar } from "@easy/ui/page-components";
import { CardToggle } from "@easy/ui/cards";
import {
    FormInput,
    FormRichText,
    FormFiles,
    FormToggleSm,
    FormAvatar,
    makeFileModel,
} from "@easy/ui/forms";
import { required, url } from "@/shared/utils/forms/i18n-vuelidators";
import { useFormValidation } from "@/shared/utils/forms/useFormValidation";
import cErrorInfo from "@/shared/components/cErrorInfo.vue";
import cSaveButton from "@/shared/components/cSaveButton.vue";
import { useFocusOnQueryField } from "@/shared/utils/forms/useFocusOnQueryField";

export default {
    components: {
        CardToggle,
        FormInput,
        FormRichText,
        FormFiles,
        FormToggleSm,
        PageSidebar,
        FormAvatar,
        cErrorInfo,
        cSaveButton,
    },
    setup() {
        useFocusOnQueryField();
        const { v$, validateForm } = useFormValidation();
        return { v$, validateForm };
    },
    validations() {
        return {
            wall: {
                webhookUrl: { url },
            },
            // this is a hack to stop vuelidate from making dirty when emails is touched
            emptyRule: { required },
        };
    },
    data() {
        return {
            files: [],
            emptyRule: "empty",
        };
    },
    computed: {
        ...mapState("love", ["wall", "hasChanges"]),
    },
    methods: {
        ...mapActions("love", ["uploadAuthorAvatar", "uploadAskImage"]),
        ...mapActions("love/projects", ["saveSnapshot"]),
        ...mapActions("alert", ["showWarnMessage"]),
        async uploadImg(file) {
            await this.uploadAskImage({ file });
        },
        removeImg() {
            this.wall.askImg = "";
        },
        changeAuthorAvatar(file) {
            if (file === null) {
                this.wall.author.avatar = undefined;
            } else {
                this.uploadAuthorAvatar({ file });
            }
        },
        handleAskForEmailUpdate(checked) {
            const { wall } = this;
            wall.askForEmail = checked;
            if (!checked && wall.emailRequired) {
                wall.emailRequired = checked;
                this.showWarnMessage({
                    message: this.$t("love.form.email_required_disabled_message"),
                });
            }
        },
        handleEmailRequiredUpdate(checked) {
            const { wall } = this;
            wall.emailRequired = checked;
            if (checked && !wall.askForEmail) {
                wall.askForEmail = checked;
                this.showWarnMessage({
                    message: this.$t("love.form.ask_for_email_confirm_message"),
                });
            }
        },
        async handleFormSave() {
            const { validateForm, saveSnapshot } = this;
            const isFormCorrect = await validateForm();
            if (isFormCorrect) {
                await saveSnapshot();
            }
        },
    },
    watch: {
        wall: {
            handler(wall, oldWall) {
                if (!oldWall?.askImg && wall?.askImg) {
                    this.files = [
                        makeFileModel({
                            url: wall.askImg,
                        }),
                    ];
                }
            },
            immediate: true,
            deep: true,
        },
    },
};
</script>
